import { postSubmit } from "@ui-seeker/utils"
import { evolutionChartVisibility, openEvolutionModal } from "Pages/AnalyzersV2/@signals"
import { batch, useSignal } from "@preact/signals-react"
import {useToast} from "../../../../../@hooks";

const DEMO_KEY_SERIES = [
	{ dataKey: 'alphacars', color: '#ef5350' },
	{ dataKey: 'peakrentals', color: '#66bb6a' },
	{ dataKey: 'eurodrive', color: '#42a5f5' },
	{ dataKey: 'horizoncars', color: '#ffa726' },
	{ dataKey: 'swiftfleet', color: '#ab47bc' },
]
const DEMO_CHART_DATA = [
	{ date: '20250101', alphacars: 175, peakrentals: 98, eurodrive: 102, horizoncars: 110, swiftfleet: 108 },
	{ date: '20250102', alphacars: 179, peakrentals: 102, eurodrive: 105, horizoncars: 106, swiftfleet: 107 },
	{ date: '20250103', alphacars: 182, peakrentals: 100, eurodrive: 108, horizoncars: 103, swiftfleet: 111 },
	{ date: '20250104', alphacars: 190, peakrentals: 96, eurodrive: 104, horizoncars: 115, swiftfleet: 105 },
	{ date: '20250105', alphacars: 185, peakrentals: 105, eurodrive: 101, horizoncars: 109, swiftfleet: 103 },
	{ date: '20250106', alphacars: 178, peakrentals: 110, eurodrive: 107, horizoncars: 112, swiftfleet: 109 },
	{ date: '20250107', alphacars: 195, peakrentals: 108, eurodrive: 110, horizoncars: 107, swiftfleet: 106 },
	{ date: '20250108', alphacars: 188, peakrentals: 103, eurodrive: 106, horizoncars: 118, swiftfleet: 114 },
	{ date: '20250109', alphacars: 192, peakrentals: 97, eurodrive: 103, horizoncars: 114, swiftfleet: 108 },
	{ date: '20250110', alphacars: 186, peakrentals: 101, eurodrive: 109, horizoncars: 111, swiftfleet: 107 },
	{ date: '20250111', alphacars: 180, peakrentals: 106, eurodrive: 112, horizoncars: 108, swiftfleet: 110 },
	{ date: '20250112', alphacars: 174, peakrentals: 99, eurodrive: 105, horizoncars: 116, swiftfleet: 112 },
	{ date: '20250113', alphacars: 183, peakrentals: 104, eurodrive: 108, horizoncars: 113, swiftfleet: 106 },
	{ date: '20250114', alphacars: 179, peakrentals: 102, eurodrive: 106, horizoncars: 106, swiftfleet: 107 },
]

const EVOLUTION_URL = '/api/price_evolution/evolution'
const EVOLUTION_BY_EXECUTION_URL = '/api/price_evolution/evolution_by_execution'

export default function useEvolutionModalData({ isRating = false } = {}) {
	const chartData = useSignal([])
	const keySeries = useSignal([])
	const isLoading = useSignal(false)
	const isVisible = useSignal(false)
	const { showToast } = useToast()
	const { filters, price_data } = openEvolutionModal.value
	const officeData = useSignal({})
	const executionDateTime = useSignal(null)
	const isByExecution = useSignal(false)
	// Only a report execution has sibling executions to compare; real time searches don't
	const canGroupByExecution = !!filters?.reportExecutionID && !filters?.searchID && !isRating && !openEvolutionModal.value.demoMode

	const setIsLoading = value => isLoading.value = value

	const setIsVisible = () => {
		if (isVisible.value || !!evolutionChartVisibility.value.length) {
			evolutionChartVisibility.value = []
			isVisible.value = false
		} else {
			evolutionChartVisibility.value = keySeries.value.map(({ dataKey }) => dataKey)
			isVisible.value = true
		}
	}

	const onSubmit = () => {
		if (openEvolutionModal.value.demoMode) {
			batch(() => {
				chartData.value = DEMO_CHART_DATA
				keySeries.value = DEMO_KEY_SERIES
				evolutionChartVisibility.value = DEMO_KEY_SERIES.map(({ dataKey }) => dataKey)
				officeData.value = {}
				executionDateTime.value = null
				isByExecution.value = false
				isLoading.value = false
			})
			return
		}
		showToast.info('Press ESC to exit fullscreen mode')
		loadEvolution()
	}

	const loadEvolution = (byExecution = false) => {
		isLoading.value = true
		postSubmit(byExecution ? EVOLUTION_BY_EXECUTION_URL : EVOLUTION_URL, {
			filters: filters,
			price_data: price_data
		}).then((res) => batch(() => {
			chartData.value = res.chartData
			keySeries.value = res.keySeries
			evolutionChartVisibility.value = res.keySeries.map(({ dataKey }) => dataKey)
			officeData.value = res.officeData
			executionDateTime.value = res.executionDateTime
			isByExecution.value = byExecution
			isLoading.value = false
		})).catch(() => {
			isLoading.value = false
		})
	}

	const toggleByExecution = () => loadEvolution(!isByExecution.value)

	return {
		onSubmit,
		toggleByExecution,
		canGroupByExecution,
		isByExecution: isByExecution.value,
		setIsVisible,
		setIsLoading,
		isVisible: isVisible.value,
		chartData: chartData.value,
		keySeries: keySeries.value,
		officeData: officeData.value,
		executionDateTime: executionDateTime.value,
		isLoading: isLoading.value
	}
}